import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { io, Socket } from 'socket.io-client';
import { getPublicApiUrl } from '../api';
import {
  CoworkOverlayName,
  CoworkOverlayPayload,
  CoworkState,
  OverlayConfig,
  OverlayElements,
  OverlaySound,
  OverlayStyle,
} from './types';
import { playCoworkSound } from './sounds';

function getSocketBase() {
  return getPublicApiUrl().replace(/\/api\/?$/, '') || window.location.origin;
}

export interface Celebration {
  id: string;
  displayName: string;
  text: string;
  at: number;
}

/** Fila de comunidad de ejemplo: la primera pendiente queda en foco. */
function demoRow(id: string, displayName: string, pending: string[], completed: string[]) {
  const tasks = [
    ...pending.map((text, index) => ({
      id: `${id}-p${index}`,
      text,
      status: 'pending' as const,
      focused: index === 0,
      order: index,
    })),
    ...completed.map((text, index) => ({
      id: `${id}-c${index}`,
      text,
      status: 'completed' as const,
      focused: false,
      order: pending.length + index,
      completedAt: new Date().toISOString(),
    })),
  ];

  return {
    twitchId: `demo-${id}`,
    displayName,
    focusText: pending[0] || null,
    tasks,
    pendingCount: pending.length,
    completedCount: completed.length,
    lastCompletedText: completed[0] || null,
    lastCompletedAt: null,
    lastActivityAt: null,
  };
}

const DEMO_STATE: CoworkState = {
  serverNow: new Date().toISOString(),
  timer: {
    status: 'running',
    phase: 'focus',
    phaseLabel: 'FOCUS',
    running: true,
    remainingMs: 42 * 60 * 1000 + 18 * 1000,
    phaseDurationMs: 50 * 60 * 1000,
    phaseEndsAt: null,
    cycle: 2,
    totalCycles: 4,
    focusCompleted: 1,
    autoStartNext: true,
    focusMinutes: 50,
    breakMinutes: 10,
    longBreakEnabled: false,
    longBreakMinutes: 20,
    cyclesBeforeLongBreak: 4,
    sessionId: null,
  },
  focusTask: {
    id: 'demo-focus',
    text: 'Preparar pedidos CeramicAlma',
    status: 'pending',
    focused: true,
    priority: 'normal',
    estimatedMinutes: 0,
    order: 2,
  },
  streamerTasks: [
    { id: 'd1', text: 'Responder mails', status: 'completed', focused: false, priority: 'normal', estimatedMinutes: 0, order: 1 },
    { id: 'demo-focus', text: 'Preparar pedidos CeramicAlma', status: 'pending', focused: true, priority: 'normal', estimatedMinutes: 0, order: 2 },
    { id: 'd3', text: 'Editar fotos', status: 'pending', focused: false, priority: 'normal', estimatedMinutes: 0, order: 3 },
    { id: 'd4', text: 'Organizar materiales', status: 'pending', focused: false, priority: 'normal', estimatedMinutes: 0, order: 4 },
    { id: 'd5', text: 'Hornear la tanda nueva', status: 'pending', focused: false, priority: 'high', estimatedMinutes: 0, order: 5 },
  ],
  community: [
    demoRow('1', 'Ana', ['Estudiar parcial', 'Leer capítulo 3', 'Pasar apuntes'], ['Responder mails', 'Ordenar escritorio']),
    demoRow('2', 'Juan', ['Editar video'], ['Guionar el corto']),
    demoRow('3', 'Sofi', ['Terminar ilustración', 'Escanear bocetos'], []),
    demoRow('4', 'Pedro', ['Responder mails', 'Cargar facturas', 'Llamar al banco', 'Actualizar planilla'], ['Backup', 'Limpiar bandeja', 'Pagar servicios']),
    demoRow('5', 'Cami', ['Practicar piano', 'Estirar 10 min'], ['Tender la ropa']),
  ],
  stats: {
    focusMs: 2 * 60 * 60 * 1000 + 35 * 60 * 1000,
    cycles: 4,
    sessions: 1,
    participants: 37,
    communityCompleted: 68,
    streamerCompleted: 5,
  },
};

const SILENT_SOUND: OverlaySound & { allowed: boolean } = {
  allowed: false,
  enabled: false,
  volume: 60,
  focusEnd: 'none',
  breakEnd: 'none',
  sessionEnd: 'none',
};

/** Completa una lista real con ejemplos hasta el mínimo pedido. */
function padWithDemo<T>(real: T[], demo: T[], min: number): T[] {
  if (real.length >= min) return real;
  return [...real, ...demo.slice(0, min - real.length)];
}

export function useCoworkOverlay(overlayKey: string | undefined, overlay: CoworkOverlayName, demo: boolean) {
  const [config, setConfig] = useState<OverlayConfig | null>(null);
  const [state, setState] = useState<CoworkState | null>(null);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const [celebrations, setCelebrations] = useState<Celebration[]>([]);

  const [previewStyle, setPreviewStyle] = useState<Partial<OverlayStyle> | null>(null);
  const [previewElements, setPreviewElements] = useState<OverlayElements | null>(null);

  const offsetRef = useRef(0);
  const lastPhaseRef = useRef<string | null>(null);
  const soundRef = useRef({ ...SILENT_SOUND });

  const applyPayload = useCallback((payload: CoworkOverlayPayload) => {
    setConfig(payload.config);
    setState(payload.state);
    offsetRef.current = Date.parse(payload.state.serverNow) - Date.now();

    const sound = payload.config.style.sound || ({} as OverlaySound);
    soundRef.current = {
      allowed: !!payload.soundEnabled,
      enabled: !!sound.enabled,
      volume: sound.volume ?? 60,
      focusEnd: sound.focusEnd || 'none',
      breakEnd: sound.breakEnd || 'none',
      sessionEnd: sound.sessionEnd || 'none',
    };
  }, []);

  const fetchAll = useCallback(async () => {
    if (!overlayKey) return;
    const apiUrl = getPublicApiUrl();
    const { data } = await axios.get<CoworkOverlayPayload>(
      `${apiUrl}/cowork/public/${overlayKey}/${overlay}`,
    );
    applyPayload(data);
    setError('');
  }, [overlayKey, overlay, applyPayload]);

  useEffect(() => {
    if (!overlayKey) return undefined;

    let socket: Socket | null = null;
    let cancelled = false;

    const setup = async () => {
      try {
        await fetchAll();
      } catch {
        if (!cancelled) setError('Overlay no encontrado o clave inválida.');
        return;
      }
      if (cancelled) return;

      socket = io(getSocketBase(), { path: '/api/socket.io' });

      socket.on('connect', () => {
        socket?.emit('join-cowork', { key: overlayKey });
        fetchAll().catch(() => undefined);
      });

      socket.on('cowork:state', (next: CoworkState) => {
        setState(next);
        offsetRef.current = Date.parse(next.serverNow) - Date.now();
      });

      socket.on('cowork:overlay-config', (data: { overlay: string }) => {
        if (data?.overlay === overlay) fetchAll().catch(() => undefined);
      });

      socket.on('cowork:task-completed', (data: { displayName?: string; text?: string }) => {
        setCelebrations((prev) => [
          ...prev.slice(-4),
          {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            displayName: data?.displayName || '',
            text: data?.text || '',
            at: Date.now(),
          },
        ]);
      });
    };

    setup();
    const poll = window.setInterval(() => {
      fetchAll().catch(() => undefined);
    }, 60000);

    return () => {
      cancelled = true;
      window.clearInterval(poll);
      if (socket) {
        socket.emit('leave-cowork', { key: overlayKey });
        socket.disconnect();
      }
    };
  }, [overlayKey, overlay, fetchAll]);

  // Reloj local: el countdown se deriva de phaseEndsAt del servidor, sin tráfico por segundo.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);

  // Limpieza de celebraciones viejas
  useEffect(() => {
    if (!celebrations.length) return undefined;
    const id = window.setTimeout(() => {
      setCelebrations((prev) => prev.filter((c) => Date.now() - c.at < 12000));
    }, 1500);
    return () => window.clearTimeout(id);
  }, [celebrations]);

  // Preview en vivo desde el editor del admin (iframe)
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== 'cowork:preview') return;
      if (data.overlay && data.overlay !== overlay) return;
      setPreviewStyle(data.style || null);
      setPreviewElements(data.elements || null);
    };
    window.addEventListener('message', onMessage);
    window.parent?.postMessage({ type: 'cowork:preview-ready', overlay }, '*');
    return () => window.removeEventListener('message', onMessage);
  }, [overlay]);

  // En modo preview los datos reales manda, pero completamos con ejemplos hasta
  // llegar a una lista densa: así el editor sirve para ajustar listas largas y
  // el modo slide aunque el stream tenga poca actividad en ese momento.
  const effectiveState = useMemo<CoworkState | null>(() => {
    if (!demo) return state;
    if (!state) return DEMO_STATE;
    return {
      ...state,
      timer: state.timer.status === 'idle' ? DEMO_STATE.timer : state.timer,
      focusTask: state.focusTask || DEMO_STATE.focusTask,
      streamerTasks: padWithDemo(
        state.streamerTasks,
        DEMO_STATE.streamerTasks.map((task) => ({ ...task, focused: false })),
        6,
      ),
      community: padWithDemo(state.community, DEMO_STATE.community, 5),
    };
  }, [demo, state]);

  const timer = effectiveState?.timer || null;

  const remainingMs = useMemo(() => {
    if (!timer) return 0;
    if (timer.status === 'running' && timer.phaseEndsAt) {
      return Math.max(0, Date.parse(timer.phaseEndsAt) - (now + offsetRef.current));
    }
    return timer.remainingMs;
  }, [timer, now]);

  // Sonido en cambios de fase (solo el overlay marcado como responsable del audio)
  useEffect(() => {
    if (!timer) return;
    const signature = `${timer.phase}:${timer.status}:${timer.cycle}`;
    if (lastPhaseRef.current === null) {
      lastPhaseRef.current = signature;
      return;
    }
    if (lastPhaseRef.current === signature) return;

    const previousPhase = lastPhaseRef.current.split(':')[0];
    lastPhaseRef.current = signature;

    const sound = soundRef.current;
    if (!sound.allowed || !sound.enabled) return;
    if (previousPhase === timer.phase) return;

    // Cada transición suena por lo que acaba de terminar, no por lo que empieza.
    if (timer.status === 'finished') {
      playCoworkSound(sound.sessionEnd, sound.volume);
    } else if (timer.phase === 'break' || timer.phase === 'long_break') {
      playCoworkSound(sound.focusEnd, sound.volume);
    } else if (timer.phase === 'focus' && previousPhase !== 'null') {
      playCoworkSound(sound.breakEnd, sound.volume);
    }
  }, [timer]);

  const style = useMemo<OverlayStyle | null>(() => {
    if (!config) return null;
    return { ...config.style, ...(previewStyle || {}) };
  }, [config, previewStyle]);

  const elements = useMemo<OverlayElements>(() => {
    if (!config) return previewElements || {};
    return { ...config.elements, ...(previewElements || {}) };
  }, [config, previewElements]);

  return {
    config,
    style,
    elements,
    state: effectiveState,
    remainingMs,
    celebrations,
    error,
    demo,
  };
}
